// Pure logic for the Channel Scanner's source-channel picker.
//
// Data comes from GET /accounts/{id}/channels — the persisted cache in front of
// CloneCast's canonical per-account source (tg.list_dialogs). Keys are always
// the canonical CloneCast account UUID (accounts[].id); never a label, phone or
// Telegram user id. Kept React-free so it can be unit-tested directly.

// Backend record: { id, title, username, type: "channel"|"group", broadcast }
export const normalizeChannels = (payload) => {
  const list = Array.isArray(payload) ? payload : payload?.dialogs || payload?.channels || [];
  return list
    .filter((c) => c && c.id != null)
    .map((c) => ({
      id: String(c.id),
      title: (c.title || "").trim() || "Untitled",
      username: c.username ? String(c.username).replace(/^@/, "") : null,
      type: c.type === "group" ? "group" : "channel",
      broadcast: Boolean(c.broadcast),
    }));
};

// Default order: title A–Z (case/accents-insensitive), never Telegram order.
const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
export const sortChannels = (list) =>
  [...list].sort((a, b) => collator.compare(a.title, b.title) || collator.compare(a.id, b.id));

// Search by title, @username (with or without the @) or Telegram id.
export const searchChannels = (list, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return list;
  const bare = q.replace(/^@/, "");
  return list.filter((c) =>
    c.title.toLowerCase().includes(q)
    || (c.username && c.username.toLowerCase().includes(bare))
    || c.id.includes(bare));
};

// Groups only use data the backend already provides (type).
export const groupChannels = (list) => ({
  channels: list.filter((c) => c.type !== "group"),
  groups: list.filter((c) => c.type === "group"),
});

// Display: title, then "@username" or "Private · ID: n".
export const channelSubtitle = (c) => (c.username ? `@${c.username}` : `Private · ID: ${c.id}`);

// How the entry link (?source=@name or ?source=123) maps onto the list.
export const matchEntrySource = (list, source) => {
  if (!source) return null;
  const s = String(source).trim();
  const bare = s.replace(/^@/, "").toLowerCase();
  return list.find((c) => c.id === s || c.id === bare
    || (c.username && c.username.toLowerCase() === bare)) || null;
};

// Empty/error state wording — every failure gets its own sentence.
export const emptyStateMessage = ({ loading, error, accountId, accountReady, count, syncedAt }) => {
  if (!accountId) return "Choose an account to load its channels.";
  if (loading) return "Loading channels…";
  if (error) return `Could not load channels — ${error}`;
  if (accountReady === false) return "Account is not runtime ready — connect it on the Accounts page.";
  if (count === 0 && !syncedAt) return "No channels loaded for this account yet.";
  if (count === 0) return "This account has no channels or groups.";
  return `${count} channel${count === 1 ? "" : "s"} available`;
};

// Scan is allowed only with an account, a channel the account can see, and a
// runtime-ready account. Returns null when allowed, else the reason code.
export const scanBlockedReason = ({ accountId, channelId, channels, accountReady, busy }) => {
  if (busy) return "BUSY";
  if (!accountId) return "NO_ACCOUNT";
  if (accountReady === false) return "ACCOUNT_NOT_READY";
  if (!channelId) return "NO_CHANNEL";
  if (!channels.some((c) => c.id === String(channelId))) return "CHANNEL_NOT_ACCESSIBLE";
  return null;
};

// Cap what gets rendered for very large lists; the search box narrows it.
export const WINDOW = 300;
export const windowList = (list, limit = WINDOW) => ({
  visible: list.slice(0, limit),
  hidden: Math.max(0, list.length - limit),
});
